import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFacebook, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { Envelope, Telephone, Whatsapp } from 'react-bootstrap-icons';
import { apiUrl } from '../../config/api';
import './Footer.scss';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Ids checked against GET /api/policy: 1 is the Shipping Policy and 2
// is the Payment Policy. These two labels were the other way round, so
// both links opened the other policy.
const CUSTOMER_SERVICE_LINKS = [
  { to: '/policies?id=1', label: 'Shipping Policy' },
  { to: '/policies?id=2', label: 'Payment Policy' },
  { to: '/policies?id=3', label: 'Return & Exchange Policy' },
  { to: '/policies?id=4', label: 'Cancellation Policy' },
  { to: '/tac', label: 'Terms & Conditions' },
  { to: '/policies?id=5', label: 'FAQs' },
];

const QUICK_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/allproducts', label: 'Shop All' },
  { to: '/all-categories', label: 'Categories' },
  { to: '/aboutus', label: 'About Us' },
  { to: '/wishlist', label: 'Wishlist' },
];

const CONTACT_LINKS = [
  { href: 'mailto:bansalattire@gmail.com', label: 'bansalattire@gmail.com', Icon: Envelope },
  { href: 'tel:+919784889134', label: '+91 97848 89134', Icon: Telephone },
  {
    href: 'https://wa.me/+919784889134',
    label: 'Chat on WhatsApp',
    Icon: Whatsapp,
    external: true,
  },
];

const SOCIAL_LINKS = [
  {
    href: 'https://www.facebook.com/share/1Xngt1LATt/?mibextid=wwXIfr',
    label: 'Bansal Attire on Facebook',
    icon: faFacebook,
  },
  {
    href: 'https://www.instagram.com/bansalattireofficial?igsh=MXFiMnVxdHYyMTF1ZQ%3D%3D&utm_source=qr',
    label: 'Bansal Attire on Instagram',
    icon: faInstagram,
  },
];

function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState({ type: null, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubscribe = async (event) => {
    event.preventDefault();

    const trimmed = email.trim();

    if (!EMAIL_PATTERN.test(trimmed)) {
      setStatus({ type: 'error', message: 'Please enter a valid email address.' });
      return;
    }

    // Guard against a double submit while the first is still in flight.
    if (isSubmitting) return;

    setIsSubmitting(true);
    setStatus({ type: null, message: '' });

    try {
      const response = await fetch(apiUrl('/api/user-subscribe'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setStatus({ type: 'success', message: 'Thank you for subscribing!' });
        setEmail('');
      } else {
        setStatus({
          type: 'error',
          message: data.message || 'Subscription failed. Please try again.',
        });
      }
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please check your connection and try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="site-footer">
      <div className="site-footer__inner">
        <div className="site-footer__brand">
          <h2 className="site-footer__wordmark">Bansal Attire</h2>
          <p className="site-footer__tagline">
            Handcrafted Indian jewellery — bridal sets, kundan, polki and temple designs,
            shipped across the USA.
          </p>
          <ul className="site-footer__contact">
            {CONTACT_LINKS.map(({ href, label, Icon, external }) => (
              <li key={href}>
                <a
                  href={href}
                  className="site-footer__contact-link"
                  {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                  <Icon size={16} aria-hidden="true" />
                  <span>{label}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>

        <nav className="site-footer__column" aria-label="Customer service">
          <h3 className="site-footer__heading">Customer Service</h3>
          <ul>
            {CUSTOMER_SERVICE_LINKS.map(({ to, label }) => (
              <li key={to}>
                <Link className="site-footer__link" to={to}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <nav className="site-footer__column" aria-label="Quick links">
          <h3 className="site-footer__heading">Explore</h3>
          <ul>
            {QUICK_LINKS.map(({ to, label }) => (
              <li key={to}>
                <Link className="site-footer__link" to={to}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="site-footer__column site-footer__newsletter">
          <h3 className="site-footer__heading">Stay in touch</h3>
          <p className="site-footer__tagline">
            Subscribe for new arrivals and exclusive offers.
          </p>

          <form className="site-footer__form" onSubmit={handleSubscribe} noValidate>
            <label className="visually-hidden" htmlFor="newsletter-email">
              Email address
            </label>
            <input
              id="newsletter-email"
              type="email"
              className="input site-footer__input"
              placeholder="you@example.com"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
                if (status.type) setStatus({ type: null, message: '' });
              }}
              aria-invalid={status.type === 'error'}
              aria-describedby={status.message ? 'newsletter-status' : undefined}
              disabled={isSubmitting}
              autoComplete="email"
            />
            <button
              type="submit"
              className="btn btn--primary"
              disabled={isSubmitting}
              aria-busy={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="btn__spinner" aria-hidden="true" />
                  Sending…
                </>
              ) : (
                'Subscribe'
              )}
            </button>
          </form>

          {status.message && (
            <p
              id="newsletter-status"
              className={`site-footer__status site-footer__status--${status.type}`}
              role={status.type === 'error' ? 'alert' : 'status'}
            >
              {status.message}
            </p>
          )}

          <ul className="site-footer__social">
            {SOCIAL_LINKS.map(({ href, label, icon }) => (
              <li key={href}>
                <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
                  <FontAwesomeIcon icon={icon} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="site-footer__bar">
        <p>&copy; {new Date().getFullYear()} Bansal Attire. All rights reserved.</p>
      </div>
    </footer>
  );
}

export default Footer;
